import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from './ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { CalendarIcon, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, X, RefreshCw, Download, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { apiService, AdminEvent, AdminEventTypeOption, AdminEventCategoryOption } from '../services/api';
import { DateRange } from 'react-day-picker';
import { getCustomerPortalBaseUrl } from '../utils/portalLink';

const BADGE_COLORS: Record<string, string> = {
  plan_changed: 'bg-blue-100 text-blue-800',
  usage_limit_reached: 'bg-red-100 text-red-800',
  usage_threshold_warning: 'bg-orange-100 text-orange-800',
  customer_suspended: 'bg-red-100 text-red-800',
  customer_resumed: 'bg-green-100 text-green-800',
  session_anonymization: 'bg-purple-100 text-purple-800',
  session_anonymization_failed: 'bg-red-100 text-red-800',
  knowledge_ingestion_failed: 'bg-red-100 text-red-800',
  tenant_created: 'bg-blue-100 text-blue-800',
  tenant_suspended: 'bg-red-100 text-red-800',
  tenant_resumed: 'bg-green-100 text-green-800',
  tenant_credential_issued: 'bg-blue-100 text-blue-800',
  tenant_credential_revoked: 'bg-red-100 text-red-800',
};

const CATEGORY_BADGE_COLORS: Record<string, string> = {
  system_audit: 'bg-slate-100 text-slate-700',
  cronjob: 'bg-indigo-100 text-indigo-800',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateShort(date: Date): string {
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function AdminEventsTable() {
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' }>({
    column: 'createdAt',
    direction: 'desc',
  });
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [columnFilters, setColumnFilters] = useState({
    searchEventType: '',
    searchCategory: '',
    searchCustomerName: '',
    searchDescription: '',
  });
  const [eventTypes, setEventTypes] = useState<AdminEventTypeOption[]>([]);
  const [eventCategories, setEventCategories] = useState<AdminEventCategoryOption[]>([]);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  // Debounce refs for text inputs
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load event types + categories on mount
  useEffect(() => {
    apiService.getAdminEventTypes().then(setEventTypes).catch(() => undefined);
    apiService.getAdminEventCategories().then(setEventCategories).catch(() => undefined);
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = {
        page,
        limit,
        sortBy: sortConfig.column,
        sortDirection: sortConfig.direction,
      };

      if (dateRange.from) {
        params.startDate = dateRange.from.toISOString();
      }
      if (dateRange.to) {
        // Set end of day
        const endOfDay = new Date(dateRange.to);
        endOfDay.setHours(23, 59, 59, 999);
        params.endDate = endOfDay.toISOString();
      }

      if (columnFilters.searchEventType) {
        params.searchEventType = columnFilters.searchEventType;
      }
      if (columnFilters.searchCategory) {
        params.searchCategory = columnFilters.searchCategory;
      }
      if (columnFilters.searchCustomerName) {
        params.searchCustomerName = columnFilters.searchCustomerName;
      }
      if (columnFilters.searchDescription) {
        params.searchDescription = columnFilters.searchDescription;
      }

      const response = await apiService.getAdminEvents(params);
      setEvents(response.data);
      setTotal(response.total);
    } catch {
      // Silently fail - events are not critical
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortConfig, dateRange, columnFilters]);

  // Fetch events on parameter changes
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchEvents, 30000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  const handleTextFilterChange = (field: 'searchCustomerName' | 'searchDescription', value: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setColumnFilters((prev) => ({ ...prev, [field]: value }));
      setPage(1);
    }, 300);
  };

  const handleSort = (column: string) => {
    setSortConfig((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1);
  };

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setDateRange({ from: range?.from, to: range?.to });
    setPage(1);
    if (range?.from && range?.to) {
      setDatePickerOpen(false);
    }
  };

  const clearDateRange = () => {
    setDateRange({});
    setPage(1);
  };

  const exportCsv = useCallback(async () => {
    try {
      setExporting(true);
      const params: Record<string, any> = {
        page: 1,
        limit: 10000,
        sortBy: sortConfig.column,
        sortDirection: sortConfig.direction,
      };
      if (dateRange.from) params.startDate = dateRange.from.toISOString();
      if (dateRange.to) {
        const endOfDay = new Date(dateRange.to);
        endOfDay.setHours(23, 59, 59, 999);
        params.endDate = endOfDay.toISOString();
      }
      if (columnFilters.searchEventType) params.searchEventType = columnFilters.searchEventType;
      if (columnFilters.searchCategory) params.searchCategory = columnFilters.searchCategory;
      if (columnFilters.searchCustomerName) params.searchCustomerName = columnFilters.searchCustomerName;
      if (columnFilters.searchDescription) params.searchDescription = columnFilters.searchDescription;

      const response = await apiService.getAdminEvents(params);

      const escape = (val: string) => `"${val.replace(/"/g, '""')}"`;
      const header = ['Zeit', 'Event', 'Kategorie', 'Kunde', 'Beschreibung'];
      const rows = response.data.map((e) => [
        formatDate(e.createdAt),
        getEventLabel(e.eventType),
        getCategoryLabel(e.category),
        e.customerName ?? 'System',
        e.description,
      ]);

      const csv = [header, ...rows].map((r) => r.map(escape).join(';')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `system-events-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Export failed silently
    } finally {
      setExporting(false);
    }
  }, [sortConfig, dateRange, columnFilters]);

  const handleImpersonate = async (event: AdminEvent) => {
    if (impersonatingId || !event.customerId) return;
    const customerId = event.customerId;
    setImpersonatingId(customerId);
    try {
      const data = await apiService.startImpersonation(
        customerId,
        `Quick impersonation from system events - ${event.customerName ?? ''}`,
      );
      window.open(
        `${getCustomerPortalBaseUrl()}/?impersonation_token=${data.token}`,
        '_blank',
        'noopener,noreferrer',
      );
      toast.success(`Impersonation gestartet für ${event.customerName}`, {
        description: 'Customer Portal in neuem Tab geöffnet',
        duration: 4000,
      });
    } catch {
      toast.error('Fehler beim Starten der Impersonation', { duration: 5000 });
    } finally {
      setImpersonatingId(null);
    }
  };

  const totalPages = Math.ceil(total / limit);
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  const SortableHeader = ({ column, label }: { column: string; label: string }) => (
    <TableHead
      className="cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.column === column && (
          sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
        )}
      </div>
    </TableHead>
  );

  const getEventLabel = (eventType: string): string => {
    const found = eventTypes.find((t) => t.type === eventType);
    return found?.label || eventType;
  };

  const getCategoryLabel = (category?: string | null): string => {
    if (!category) return '—';
    const found = eventCategories.find((c) => c.category === category);
    return found?.label ?? category;
  };

  // Pagination page numbers
  const getPageNumbers = (): number[] => {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  const dateRangeLabel = dateRange.from
    ? dateRange.to
      ? `${formatDateShort(dateRange.from)} - ${formatDateShort(dateRange.to)}`
      : formatDateShort(dateRange.from)
    : 'Zeitraum wählen';

  return (
    <Card className="flex flex-col h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">System Events</CardTitle>
          <div className="flex items-center gap-2">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 text-sm">
                  <CalendarIcon className="w-4 h-4" />
                  {dateRangeLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange.from ? { from: dateRange.from, to: dateRange.to } : undefined}
                  onSelect={handleDateRangeSelect}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            {dateRange.from && (
              <Button variant="ghost" size="sm" onClick={clearDateRange} className="h-8 w-8 p-0">
                <X className="w-4 h-4" />
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={exporting} className="h-8 w-8 p-0" title="Als CSV exportieren">
              <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" onClick={fetchEvents} disabled={loading} className="h-8 w-8 p-0" title="Aktualisieren">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHeader column="createdAt" label="Zeit" />
                <SortableHeader column="eventType" label="Event" />
                <SortableHeader column="category" label="Kategorie" />
                <SortableHeader column="customerName" label="Kunde" />
                <TableHead>Beschreibung</TableHead>
                <TableHead className="w-10" />
              </TableRow>
              {/* Column search row */}
              <TableRow>
                <TableHead />
                <TableHead className="p-1">
                  <Select
                    value={columnFilters.searchEventType}
                    onValueChange={(value) => {
                      setColumnFilters((prev) => ({ ...prev, searchEventType: value === 'all' ? '' : value }));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      {eventTypes.map((et) => (
                        <SelectItem key={et.type} value={et.type}>
                          {et.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableHead>
                <TableHead className="p-1">
                  <Select
                    value={columnFilters.searchCategory}
                    onValueChange={(value) => {
                      setColumnFilters((prev) => ({ ...prev, searchCategory: value === 'all' ? '' : value }));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      {eventCategories.map((c) => (
                        <SelectItem key={c.category} value={c.category}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableHead>
                <TableHead className="p-1">
                  <Input
                    placeholder="Suchen..."
                    className="h-8 text-xs"
                    defaultValue={columnFilters.searchCustomerName}
                    onChange={(e) => handleTextFilterChange('searchCustomerName', e.target.value)}
                  />
                </TableHead>
                <TableHead className="p-1">
                  <Input
                    placeholder="Suchen..."
                    className="h-8 text-xs"
                    defaultValue={columnFilters.searchDescription}
                    onChange={(e) => handleTextFilterChange('searchDescription', e.target.value)}
                  />
                </TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-6" /></TableCell>
                  </TableRow>
                ))
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Keine Events im gewählten Zeitraum
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(event.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Badge className={BADGE_COLORS[event.eventType] || 'bg-gray-100 text-gray-800'}>
                        {getEventLabel(event.eventType)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {event.category ? (
                        <Badge className={CATEGORY_BADGE_COLORS[event.category] || 'bg-gray-100 text-gray-800'}>
                          {getCategoryLabel(event.category)}
                        </Badge>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{event.customerName ?? 'System'}</TableCell>
                    <TableCell className="text-sm max-w-xs truncate" title={event.description}>
                      {event.description}
                    </TableCell>
                    <TableCell className="p-1">
                      {event.customerId ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title={`Als ${event.customerName ?? 'Kunde'} anmelden`}
                          disabled={impersonatingId === event.customerId}
                          onClick={() => handleImpersonate(event)}
                        >
                          <UserCheck className="w-4 h-4" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between pt-3 border-t mt-auto">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>{startItem}-{endItem} von {total} Events</span>
              <Select
                value={limit.toString()}
                onValueChange={(value) => {
                  setLimit(Number(value));
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-[70px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {getPageNumbers().map((p) => (
                <Button
                  key={p}
                  variant={p === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(p)}
                  className="h-8 w-8 p-0 text-xs"
                >
                  {p}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
